import {
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
} from 'react'

import { type EntityRef } from '../../lib/use-entity-recents'

type Pending = { firstChildOf: string }

const TYPEAHEAD_RESET_MS = 500

function focusRow(row: HTMLElement | null | undefined): void {
  if (!row) return
  row.focus({ preventScroll: true })
  row.scrollIntoView({ block: 'nearest' })
}


export function useTreeKeyboard({
  treeRef,
  onToggleExpanded,
  onSelect,
  fallbackActiveId,
}: {
  treeRef: RefObject<HTMLElement | null>
  onToggleExpanded: (id: string) => void
  onSelect: (ref: EntityRef) => void
  fallbackActiveId: string | null
}) {
  const pendingRef = useRef<Pending | null>(null)
  const typeaheadRef = useRef<{
    buffer: string
    timer: ReturnType<typeof setTimeout> | null
  }>({ buffer: '', timer: null })
  const [activeIdState, setActiveIdState] = useState<string | null>(null)

  const activeId = activeIdState ?? fallbackActiveId

  useEffect(() => {
    const typeahead = typeaheadRef.current
    return () => {
      if (typeahead.timer) clearTimeout(typeahead.timer)
    }
  }, [])

  function rows(): HTMLElement[] {
    const root = treeRef.current
    if (!root) return []
    return Array.from(root.querySelectorAll<HTMLElement>('[role="treeitem"]'))
  }

  function levelOf(el: HTMLElement): number {
    return Number(el.getAttribute('aria-level') ?? '1')
  }

  function currentRow(list: HTMLElement[]): HTMLElement | null {
    const root = treeRef.current
    const focused = document.activeElement
    if (
      root &&
      focused instanceof HTMLElement &&
      root.contains(focused) &&
      focused.matches('[role="treeitem"]')
    )
      return focused
    return list.find((el) => el.dataset.entityId === activeId) ?? list[0] ?? null
  }

  function selectRow(el: HTMLElement): void {
    if (el.getAttribute('aria-disabled') === 'true') return
    const { entityId, name } = el.dataset
    if (entityId && name !== undefined) onSelect({ id: entityId, name })
  }

  function runTypeahead(char: string): void {
    const state = typeaheadRef.current
    if (state.timer) clearTimeout(state.timer)
    state.buffer += char.toLowerCase()
    state.timer = setTimeout(() => {
      state.buffer = ''
      state.timer = null
    }, TYPEAHEAD_RESET_MS)

    const list = rows()
    const start = list.indexOf(currentRow(list) ?? list[0]!)
    for (let i = 1; i <= list.length; i++) {
      const el = list[(start + i) % list.length]
      if (el?.dataset.name?.toLowerCase().startsWith(state.buffer)) {
        focusRow(el)
        return
      }
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.altKey || e.ctrlKey || e.metaKey) return
    const list = rows()
    if (list.length === 0) return
    const current = currentRow(list)
    if (!current) return
    const idx = list.indexOf(current)

    pendingRef.current = null

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        focusRow(list[idx + 1])
        break
      case 'ArrowUp':
        e.preventDefault()
        focusRow(list[idx - 1])
        break
      case 'Home':
        e.preventDefault()
        focusRow(list[0])
        break
      case 'End':
        e.preventDefault()
        focusRow(list[list.length - 1])
        break
      case 'ArrowRight': {
        e.preventDefault()
        const expanded = current.getAttribute('aria-expanded')
        if (expanded === 'false') {
          onToggleExpanded(current.dataset.entityId!)
        } else if (expanded === 'true') {
          const next = list[idx + 1]
          if (next && levelOf(next) > levelOf(current)) focusRow(next)
          else pendingRef.current = { firstChildOf: current.dataset.entityId! }
        }
        break
      }
      case 'ArrowLeft':
        e.preventDefault()
        if (current.getAttribute('aria-expanded') === 'true') {
          onToggleExpanded(current.dataset.entityId!)
        } else {
          const level = levelOf(current)
          for (let i = idx - 1; i >= 0; i--) {
            if (levelOf(list[i]!) < level) {
              focusRow(list[i])
              break
            }
          }
        }
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        selectRow(current)
        break
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          e.preventDefault()
          runTypeahead(e.key)
        }
    }
  }

  function onClick(e: MouseEvent<HTMLDivElement>): void {
    const item = (e.target as HTMLElement).closest<HTMLElement>(
      '[role="treeitem"]',
    )
    if (!item) return
    const id = item.dataset.entityId
    if (!id) return
    if (
      item.hasAttribute('aria-expanded') &&
      (e.target as HTMLElement).closest('[data-tree-chevron]')
    ) {
      onToggleExpanded(id)
      return
    }
    selectRow(item)
  }

  function onFocus(e: FocusEvent<HTMLDivElement>): void {
    const item = (e.target as HTMLElement).closest<HTMLElement>(
      '[role="treeitem"]',
    )
    if (item?.dataset.entityId) setActiveIdState(item.dataset.entityId)
  }

  function registerNode(el: HTMLElement | null): void {
    if (!el) return
    const { entityId, parentId } = el.dataset
    if (!entityId) return
    if (pendingRef.current && parentId === pendingRef.current.firstChildOf) {
      pendingRef.current = null
      focusRow(el)
    }
  }

  return { activeId, onKeyDown, onClick, onFocus, registerNode }
}
